/**
 * Markdown → document blocks (Lexical JSON), the reverse of
 * blocksToMarkdown.js (git sync Stage 2, S2.1). Reads what the writer
 * produces exactly, and ordinary hand-written Markdown sensibly (a person
 * editing the .md in VS Code or on GitHub). Pure: images and page links are
 * resolved through `ctx`; the result still goes through docEditor.normalize.
 */
import { marked } from 'marked';
import * as J from './jsonNodes';
import { FMT } from './inline';
import { FALLBACK_LANG, PAGE_MARK, UPLOAD_ANCHOR } from './blocksToMarkdown';
import { parseVideoUrl } from '../videoEmbeds';
import { calloutOf } from './callout';

const NAMED = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#39': "'" };
export function decodeEntities(s) {
  return String(s).replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[a-zA-Z]+);/g, (m, e) => {
    if (e[0] === '#') {
      const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : m;
    }
    return NAMED[e] ?? m;
  });
}

// marked turns tabs into spaces before it tokenizes (even in code); a tab is
// swapped for a private-use character first and put back in every text.
const TAB_MARK = String.fromCharCode(0xe009);
const untab = (s) => String(s).split(TAB_MARK).join(String.fromCharCode(9));

/** A new media file's pendingRef (the node allows [A-Za-z0-9_-]{6,64}): stable per path. */
export function gitMediaRef(path) {
  let h = 0x811c9dc5;
  for (const ch of String(path)) {
    h ^= ch.codePointAt(0);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `git-${h.toString(36)}${String(path).length.toString(36)}`;
}

const TAG_FMT = { b: FMT.bold, strong: FMT.bold, i: FMT.italic, em: FMT.italic, s: FMT.strike, del: FMT.strike, strike: FMT.strike, u: FMT.underline, sub: FMT.sub, sup: FMT.sup, mark: FMT.mark, code: FMT.code };

/* ── what a hand-written file may bring in ─────────────────────────────── */

/** Links: http(s), mailto and relative addresses only — anything else (javascript:, data:…) stays text. */
const SAFE_HREF = /^(https?:|mailto:)/i;
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;
export const isSafeHref = (href) => {
  const h = String(href || '').trim();
  return Boolean(h) && (SAFE_HREF.test(h) || !HAS_SCHEME.test(h));
};

/** Inline styles: nothing that can fetch or run (url(), expression(), javascript:). */
export const safeStyle = (style) => (/url\s*\(|expression\s*\(|javascript:/i.test(String(style || '')) ? '' : String(style || ''));

/**
 * A media path in the repository: relative, inside it. Absolute paths, `..`
 * and URLs are not files of this repository — the sync layer must never be
 * asked to upload them (review of #372).
 */
export function isRepoPath(path) {
  const p = String(path || '');
  if (!p || HAS_SCHEME.test(p) || /^[\\/]/.test(p) || /^[a-zA-Z]:/.test(p)) return false;
  return !p.split(/[\\/]/).some((seg) => seg === '..');
}

/** A link node, or — for an unsafe address — just its text. */
function linkOrText(href, kids, opts) {
  return isSafeHref(href) ? [J.link(href, kids, opts)] : kids;
}

/* ── inline ────────────────────────────────────────────────────────────── */

/**
 * Inline tokens → inline JSON. Inline HTML arrives as separate open/close
 * tokens (`<u>`, `</u>`), so formats from tags live on a stack.
 */
function inlineFromTokens(tokens, ctx) {
  const out = [];
  const stack = [{ fmt: 0, style: '', target: out }];
  const top = () => stack[stack.length - 1];
  const pushText = (raw, extraFmt = 0) => {
    if (!raw) return;
    const t = untab(raw);
    const { fmt, style, target } = top();
    const f = fmt | extraFmt;
    const parts = t.split('\t');
    parts.forEach((part, i) => {
      if (i > 0) target.push(J.tab());
      if (part) target.push(J.text(part, f, style));
    });
  };

  const walk = (toks, addFmt) => {
    for (const tok of toks || []) {
      switch (tok.type) {
        case 'text':
          if (tok.tokens && tok.tokens.length) walk(tok.tokens, addFmt);
          else pushText(decodeEntities(tok.text), addFmt);
          break;
        case 'escape':
          // marked hands an escaped < > & back HTML-escaped (\< → "&lt;")
          pushText(decodeEntities(tok.text), addFmt);
          break;
        case 'strong':
          walk(tok.tokens, addFmt | FMT.bold);
          break;
        case 'em':
          walk(tok.tokens, addFmt | FMT.italic);
          break;
        case 'del':
          walk(tok.tokens, addFmt | FMT.strike);
          break;
        case 'codespan':
          pushText(decodeEntities(tok.text), addFmt | FMT.code);
          break;
        case 'br':
          top().target.push(J.linebreak());
          break;
        case 'link': {
          const page = ctx.pageLinkFromHref(decodeURIHref(tok.href));
          if (page) {
            top().target.push(J.pageLink({ ...page, title: plainText(tok.tokens) }));
          } else {
            const kids = [];
            stack.push({ ...top(), target: kids });
            walk(tok.tokens, addFmt);
            stack.pop();
            top().target.push(...linkOrText(tok.href, kids, { title: tok.title || null }));
          }
          break;
        }
        case 'image':
          // An image inside running text has no node of its own in the editor: keep it as text.
          pushText(tok.raw, addFmt);
          break;
        case 'html':
          handleInlineHtml(tok.text, addFmt);
          break;
        default:
          pushText(tok.raw || tok.text || '', addFmt);
      }
    }
  };

  const handleInlineHtml = (html, addFmt) => {
    const t = html.trim();
    if (/^<br\s*\/?>$/i.test(t)) {
      top().target.push(J.linebreak());
      return;
    }
    const close = /^<\/([a-z]+)\s*>$/i.exec(t);
    if (close) {
      const name = close[1].toLowerCase();
      // pop up to (and including) the matching open tag
      for (let i = stack.length - 1; i > 0; i -= 1) {
        if (stack[i].tag === name) {
          const frame = stack[i];
          stack.length = i;
          if (name === 'a' && frame.link) {
            const page = ctx.pageLinkFromHref(frame.link.href);
            if (page) top().target.push(J.pageLink({ ...page, title: plainFromJson(frame.target) }));
            else top().target.push(...linkOrText(frame.link.href, frame.target, frame.link));
          }
          return;
        }
      }
      return;
    }
    const open = /^<([a-z]+)((?:\s+[a-z-]+(?:="[^"]*")?)*)\s*>$/i.exec(t);
    if (open) {
      const name = open[1].toLowerCase();
      const attrs = parseAttrs(open[2]);
      if (TAG_FMT[name] !== undefined) {
        stack.push({ ...top(), tag: name, fmt: top().fmt | TAG_FMT[name] });
        return;
      }
      if (name === 'span') {
        stack.push({ ...top(), tag: name, style: safeStyle(attrs.style) || top().style });
        return;
      }
      if (name === 'a') {
        stack.push({ ...top(), tag: 'a', target: [], link: { href: attrs.href || '', title: attrs.title || null, target: attrs.target || null, rel: attrs.rel || null } });
        return;
      }
    }
    // Anything else is text the person wrote.
    pushText(decodeEntities(html), addFmt);
  };

  walk(tokens, 0);
  return out;
}

function parseAttrs(s) {
  const out = {};
  String(s || '').replace(/([a-z-]+)(?:="([^"]*)")?/gi, (m, k, v) => {
    out[k.toLowerCase()] = v === undefined ? true : decodeEntities(v);
    return m;
  });
  return out;
}

const plainText = (tokens) =>
  (tokens || []).map((t) => (t.tokens ? plainText(t.tokens) : decodeEntities(t.type === 'escape' ? t.text : t.text || ''))).join('');
const plainFromJson = (nodes) => (nodes || []).map((n) => n.text || plainFromJson(n.children)).join('');
const decodeURIHref = (h) => {
  try {
    return decodeURI(h);
  } catch {
    return h;
  }
};

/* ── blocks ────────────────────────────────────────────────────────────── */

const ATTR_COMMENT = /^<!--\s*apiboo((?:\s+[a-z]+=[^\s]+)*)\s*-->\s*$/i;

/**
 * @param {string} md
 * @param {object} ctx  { pageLinkFromHref(href) → {docId,scopeType,teamId}|null, mediaFor({path?, src?}) → fields|null }
 * @returns {Array} block JSON (not yet normalized)
 */
export function markdownToBlocks(md, ctx) {
  const tokens = marked.lexer(String(md || '').split(String.fromCharCode(9)).join(TAB_MARK), { gfm: true });
  const blocks = [];
  let pending = null; // attrs from an <!-- apiboo … --> line

  const add = (node) => {
    if (pending && node && node.type !== 'list' && 'indent' in node) {
      if (pending.indent) node.indent = Number(pending.indent);
      if (pending.align) node.format = pending.align;
    }
    pending = null;
    if (node) blocks.push(node);
  };

  const handle = (tok) => {
    switch (tok.type) {
      case 'space':
      case 'def':
        break;
      case 'heading':
        add(J.heading(`h${tok.depth}`, inlineFromTokens(tok.tokens, ctx)));
        break;
      case 'paragraph':
        add(paragraphBlock(tok, ctx));
        break;
      case 'blockquote': {
        // The editor's quote holds text only: its paragraphs form the quote; a
        // list or code block someone put inside it follows as its own block —
        // never dropped (review of #372). A `> [!NOTE]` quote is a callout
        // (callout.js) and reads the same way.
        const callout = calloutOf(tok);
        let kids = [];
        let added = false;
        const flush = () => {
          if (kids.length || (callout && !added)) add(callout ? J.callout(callout.kind, kids) : J.quote(kids));
          if (kids.length || callout) added = true;
          kids = [];
        };
        for (const child of callout ? callout.tokens : tok.tokens || []) {
          if (child.type === 'paragraph' || child.type === 'text') {
            if (kids.length) kids.push(J.linebreak());
            kids.push(...inlineFromTokens(child.tokens || [{ type: 'text', text: child.text }], ctx));
          } else if (child.type !== 'space') {
            flush();
            handle(child);
          }
        }
        flush();
        break;
      }
      case 'list':
        add(listBlock(tok, ctx));
        break;
      case 'code':
        if (tok.lang === FALLBACK_LANG) add(fallbackFromJson(untab(tok.text), ctx));
        else add(J.code(tok.lang || '', untab(codeFromRaw(tok))));
        break;
      case 'table':
        add(pipeTable(tok, ctx));
        break;
      case 'html': {
        const html = tok.text.trim();
        const attr = ATTR_COMMENT.exec(html);
        if (attr) {
          pending = Object.fromEntries(attr[1].trim().split(/\s+/).filter(Boolean).map((kv) => kv.split('=')));
          break;
        }
        if (/^<br\s*\/?>$/i.test(html)) add(J.paragraph([]));
        else if (/^<table[\s>]/i.test(html)) add(htmlTable(html, ctx));
        else if (/^<(img|video)\s/i.test(html)) add(mediaFromHtml(html, ctx));
        else if (/^<!--/.test(html)) break; // a comment someone wrote
        else add(J.paragraph(inlineFromTokens([{ type: 'html', text: html }], ctx)));
        break;
      }
      case 'hr':
        add(J.paragraph([J.text(tok.raw.trim())]));
        break;
      default:
        add(J.paragraph([J.text(tok.raw || '')]));
    }
  };
  for (const tok of tokens) handle(tok);
  return blocks;
}

/**
 * An `apiboo-block` (the exact JSON form) back into a block. Image keys were
 * left out on write; they come back from the document as it is now. JSON that
 * does not parse (a broken hand edit) stays as visible text in a code block —
 * one bad block never fails the whole document.
 */
function fallbackFromJson(text, ctx) {
  let node;
  try {
    node = JSON.parse(text);
  } catch {
    return J.code(FALLBACK_LANG, text);
  }
  if (!node || typeof node !== 'object' || Array.isArray(node) || typeof node.type !== 'string') {
    return J.code(FALLBACK_LANG, text);
  }
  return withMediaKeys(node, ctx);
}

function withMediaKeys(node, ctx) {
  if (!node || typeof node !== 'object') return node;
  if (Array.isArray(node)) return node.map((n) => withMediaKeys(n, ctx));
  if (node.type === 'doc-image') {
    const known = ctx.mediaFor({ src: node.src || null, pendingRef: node.pendingRef || null });
    return known ? { ...node, k: known.k || '' } : { ...node, k: '' };
  }
  const out = { ...node };
  if (Array.isArray(node.children)) out.children = node.children.map((n) => withMediaKeys(n, ctx));
  return out;
}

/**
 * A fenced block's text exactly as written: marked turns tabs into spaces in
 * tok.text, so the lines between the fences are taken from tok.raw.
 */
function codeFromRaw(tok) {
  const raw = String(tok.raw || '').replace(/\n+$/, '');
  const open = /^ {0,3}(`{3,}|~{3,})[^\n]*\n/.exec(raw);
  if (!open) return tok.text;
  const body = raw.slice(open[0].length);
  const fenceChar = open[1][0] === '`' ? '`' : '~';
  const close = new RegExp(`\\n? {0,3}${fenceChar}{${open[1].length},}\\s*$`);
  return body.replace(close, '');
}

function paragraphBlock(tok, ctx) {
  const toks = (tok.tokens || []).filter((t) => !(t.type === 'text' && t.text === '' ));
  // ![alt](path) alone → an image block
  if (toks.length === 1 && toks[0].type === 'image') {
    return mediaNode({ path: decodeURIHref(toks[0].href), alt: decodeEntities(toks[0].text || '') }, ctx);
  }
  // <video …></video> / <img …> alone (inline HTML tokens)
  if (toks.length && toks.every((t) => t.type === 'html') && /^<(video|img)\s/i.test(toks[0].text.trim())) {
    return mediaFromHtml(toks.map((t) => t.text).join(''), ctx);
  }
  // 📄 [Title](href) alone → a page-link block
  if (toks.length === 2 && toks[0].type === 'text' && toks[0].text === `${PAGE_MARK} ` && toks[1].type === 'link') {
    const page = ctx.pageLinkFromHref(decodeURIHref(toks[1].href));
    if (page) return J.pageLink({ ...page, title: plainText(toks[1].tokens) });
  }
  if (toks.length === 1 && toks[0].type === 'link') {
    const label = plainText(toks[0].tokens);
    // [▶ YouTube](watch url) alone → an embed
    if (/^▶ (YouTube|Vimeo)$/.test(label)) {
      const v = parseVideoUrl(toks[0].href);
      if (v) return J.docVideo({ provider: v.provider, videoId: v.id, start: v.start || 0 });
    }
    // [▶ Video: name — kept in Apiboo](src) → a media node that stays in Apiboo
    if (/^(▶ Video|🖼 Image): .* — kept in Apiboo$/.test(label)) {
      const known = ctx.mediaFor({ src: toks[0].href });
      if (known) return J.docImage(known);
    }
    // [🖼 Image: name — not uploaded yet](#apiboo-upload-<ref>) → that same image, still uploading
    const upload = String(toks[0].href || '').match(new RegExp(`^#${UPLOAD_ANCHOR}([A-Za-z0-9_-]{6,64})$`));
    if (upload && /^(▶ Video|🖼 Image): .* — not uploaded yet$/.test(label)) {
      const known = ctx.mediaFor({ pendingRef: upload[1] });
      if (known) return J.docImage(known);
    }
  }
  return J.paragraph(inlineFromTokens(tok.tokens, ctx));
}

/** Block tokens (a quote's paragraphs) as one run of inline tokens, lines separated by breaks. */
function flattenBlockTokens(tokens) {
  const out = [];
  for (const t of tokens) {
    if (t.type === 'space') continue;
    if (out.length) out.push({ type: 'br' });
    out.push(...(t.tokens && t.tokens.length ? t.tokens : [{ type: 'text', text: t.text || t.raw || '' }]));
  }
  return out;
}

function listBlock(tok, ctx) {
  const isCheck = tok.items.some((it) => it.task);
  const listType = isCheck ? 'check' : tok.ordered ? 'number' : 'bullet';
  const items = [];
  for (const it of tok.items) {
    const inlineToks = [];
    const nested = [];
    for (const t of it.tokens || []) {
      if (t.type === 'list') nested.push(t);
      else if (t.type === 'text' || t.type === 'paragraph') {
        if (inlineToks.length) inlineToks.push({ type: 'br' });
        inlineToks.push(...(t.tokens || [{ type: 'text', text: t.text }]));
      } else if (t.type === 'code') {
        // An editor list item holds text only: a code block in it becomes code-formatted lines (review of #372).
        untab(codeFromRaw(t)).split('\n').forEach((line) => {
          if (inlineToks.length) inlineToks.push({ type: 'br' });
          if (line) inlineToks.push({ type: 'codespan', text: line.replace(/&/g, '&amp;').replace(/</g, '&lt;') });
        });
      } else if (t.type !== 'space') {
        // A quote or anything else in an item: its text, so nothing written there is lost.
        if (inlineToks.length) inlineToks.push({ type: 'br' });
        inlineToks.push(...(t.tokens && t.tokens.length ? flattenBlockTokens(t.tokens) : [{ type: 'text', text: t.raw || t.text || '' }]));
      }
    }
    items.push(J.listItem(inlineFromTokens(inlineToks, ctx), isCheck ? Boolean(it.checked) : undefined));
    // Lexical keeps a nested list in its own wrapper item.
    for (const n of nested) items.push(J.listItem([listBlock(n, ctx)]));
  }
  const start = tok.ordered && Number.isFinite(Number(tok.start)) && tok.start !== '' ? Number(tok.start) : 1;
  return J.list(listType, items, start);
}

function pipeTable(tok, ctx) {
  const cell = (c, headerState) => J.tableCell([J.paragraph(inlineFromTokens(c.tokens, ctx))], { headerState });
  return J.table([
    J.tableRow(tok.header.map((c) => cell(c, 1))),
    ...tok.rows.map((r) => J.tableRow(r.map((c) => cell(c, 0)))),
  ]);
}

/* ── HTML blocks: tables and media ─────────────────────────────────────── */

function parseHtml(html) {
  return new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body;
}

/** DOM inline content → inline JSON (cells of an HTML table). */
function inlineFromDom(nodeList, ctx, fmt = 0, style = '') {
  const out = [];
  for (const n of Array.from(nodeList)) {
    if (n.nodeType === 3) {
      const parts = untab(n.nodeValue).split('\t');
      parts.forEach((p, i) => {
        if (i > 0) out.push(J.tab());
        if (p) out.push(J.text(p, fmt, style));
      });
      continue;
    }
    if (n.nodeType !== 1) continue;
    const tag = n.tagName.toLowerCase();
    if (tag === 'br') out.push(J.linebreak());
    else if (TAG_FMT[tag] !== undefined) out.push(...inlineFromDom(n.childNodes, ctx, fmt | TAG_FMT[tag], style));
    else if (tag === 'span') out.push(...inlineFromDom(n.childNodes, ctx, fmt, safeStyle(n.getAttribute('style')) || style));
    else if (tag === 'a') {
      const href = n.getAttribute('href') || '';
      const page = ctx.pageLinkFromHref(href);
      if (page) out.push(J.pageLink({ ...page, title: n.textContent }));
      else out.push(...linkOrText(href, inlineFromDom(n.childNodes, ctx, fmt, style), {
        title: n.getAttribute('title'), target: n.getAttribute('target'), rel: n.getAttribute('rel'),
      }));
    } else out.push(...inlineFromDom(n.childNodes, ctx, fmt, style));
  }
  return out;
}

function htmlTable(html, ctx) {
  const tableEl = parseHtml(html).querySelector('table');
  let extra = {};
  try {
    extra = tableEl.getAttribute('data-apiboo') ? JSON.parse(tableEl.getAttribute('data-apiboo')) : {};
  } catch {
    extra = {};
  }
  // Table settings only — never the node's type, children or structure (review of #372).
  extra = Object.fromEntries(
    Object.entries(extra && typeof extra === 'object' && !Array.isArray(extra) ? extra : {})
      .filter(([k]) => !['type', 'children', 'version', 'direction', 'format', 'indent'].includes(k))
  );
  const rows = Array.from(tableEl.querySelectorAll('tr')).map((tr) =>
    J.tableRow(Array.from(tr.children).filter((c) => /^t[hd]$/i.test(c.tagName)).map((c) => {
      const isTh = c.tagName.toLowerCase() === 'th';
      const bg = /background-color:\s*([^;]+)/i.exec(c.getAttribute('style') || '');
      const paras = Array.from(c.children).filter((p) => p.tagName.toLowerCase() === 'p');
      const blocks = (paras.length ? paras : [c]).map((p) => {
        const attrs = {};
        if (p.getAttribute && p.getAttribute('data-indent')) attrs.indent = Number(p.getAttribute('data-indent'));
        if (p.getAttribute && p.getAttribute('data-align')) attrs.format = p.getAttribute('data-align');
        return J.paragraph(inlineFromDom(p.childNodes, ctx), attrs);
      });
      return J.tableCell(blocks, {
        headerState: c.getAttribute('data-header') ? Number(c.getAttribute('data-header')) : isTh ? 1 : 0,
        colSpan: Number(c.getAttribute('colspan') || 1),
        rowSpan: Number(c.getAttribute('rowspan') || 1),
        backgroundColor: bg ? bg[1].trim() : null,
        width: c.getAttribute('width') ? Number(c.getAttribute('width')) : undefined,
      });
    }))
  );
  return J.table(rows, extra);
}

function mediaFromHtml(html, ctx) {
  const el = parseHtml(html).querySelector('img, video');
  if (!el) return J.paragraph([J.text(html)]);
  const width = el.getAttribute('width') ? Number(el.getAttribute('width')) : null;
  return mediaNode({ path: el.getAttribute('src') || '', alt: el.getAttribute('alt') || '', width, video: el.tagName.toLowerCase() === 'video' }, ctx);
}

/** A media file in the repo → the image node: known (same upload) or new (to be uploaded by the sync layer). */
function mediaNode({ path, alt = '', width = null, video = false }, ctx) {
  // ctx.isMediaHref: the caller's rule (a scope repo resolves links relative to the .md file).
  if (!(ctx.isMediaHref || isRepoPath)(path)) return J.paragraph([J.text(video ? `[video: ${path}]` : `![${alt}](${path})`)]);
  const known = ctx.mediaFor({ path });
  if (known) return J.docImage({ ...known, alt: video ? known.alt : alt, width });
  const name = path.split('/').pop();
  const pendingRef = gitMediaRef(path);
  if (ctx.onNewMedia) ctx.onNewMedia({ pendingRef, path });
  return J.docImage({ pendingRef, name, mime: video ? 'video/mp4' : guessMime(name), alt, width });
}

function guessMime(name) {
  const ext = String(name).split('.').pop().toLowerCase();
  return { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml', mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime' }[ext] || 'application/octet-stream';
}
